import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Sparkles, X, Copy, CheckCircle2, XCircle, Check, ChevronDown,
  ChevronLeft, RefreshCw, UserRound, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import SafeImage from '../../../components/SafeImage';

/* -------------------- FULL-SCREEN WHITE PANEL -------------------- */
export function FullScreenWhitePanel({ children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: '#FFFFFF', color: '#0B0F0C', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/* -------------------- STEPPER (dynamic 2 or 3 steps) -------------------- */
export function Stepper({ step, accent, accentDark, labels = ['Payment method', 'Pay'] }) {
  const Step = ({ n, label, state }) => {
    const isActive = state === 'active';
    const isDone = state === 'done';
    const ringColor = isActive || isDone ? accent : '#D1D5DB';
    const fillColor = isDone ? accent : 'transparent';
    const numColor = isDone ? '#fff' : (isActive ? accentDark : '#9CA3AF');
    const labelColor = isActive || isDone ? accentDark : '#9CA3AF';
    return (
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center display font-extrabold text-base transition-all"
          style={{
            border: `3px solid ${ringColor}`,
            background: fillColor,
            color: numColor,
            boxShadow: isActive ? `0 0 0 6px ${accent}1F` : 'none'
          }}
          data-testid={`stepper-${n}`}
        >
          {isDone ? <Check className="w-5 h-5" strokeWidth={4} /> : n}
        </div>
        <span className="text-xs sm:text-sm display font-bold tracking-wide text-center" style={{ color: labelColor }}>
          {label}
        </span>
      </div>
    );
  };
  return (
    <div className="flex items-start justify-center gap-2 sm:gap-3 mb-2" data-testid="wallet-stepper">
      {labels.map((label, idx) => {
        const n = idx + 1;
        const state = step > n ? 'done' : step === n ? 'active' : 'idle';
        return (
          <React.Fragment key={n}>
            <Step n={n} label={label} state={state} />
            {idx < labels.length - 1 && (
              <div
                className="flex-1 max-w-[140px] h-[3px] rounded-full mt-[22px] transition-colors"
                style={{ background: step > n ? accent : '#E5E7EB' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* -------------------- ROW (small label/value pair) -------------------- */
export function Row({ label, value, valueBold, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="uppercase tracking-wider text-xs font-bold" style={{ color: '#6B7280' }}>{label}</span>
      <span
        className={`text-right ${mono ? 'mono' : ''} ${valueBold ? 'display font-extrabold' : 'font-medium'}`}
        style={{ color: '#0B0F0C', wordBreak: 'break-word', maxWidth: '60%' }}
      >
        {value}
      </span>
    </div>
  );
}

/* -------------------- PAYMENT METHOD LOGO --------------------
   Renders an authentic-looking brand mark for the supported gateway
   methods (cashapp / applepay / googlepay), and falls back to a
   generic Wallet icon for tenant-custom manual methods.
*/
export function PaymentMethodLogo({ method, accent, accentDark, size = 'md' }) {
  const code = (method?.method || method?.gateway_method || '').toLowerCase();
  const dim = size === 'sm' ? 44 : 48;
  const iconPx = size === 'sm' ? 22 : 26;
  const rawLogoUrl = String(method?.logo_url || '').trim();
  const resolvedLogoUrl = rawLogoUrl
    ? (rawLogoUrl.startsWith('http://') || rawLogoUrl.startsWith('https://')
        ? rawLogoUrl
        : `${process.env.REACT_APP_BACKEND_URL || ''}${rawLogoUrl}`)
    : '';

  if (resolvedLogoUrl) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
        style={{
          width: dim,
          height: dim,
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.10)',
          boxShadow: '0 4px 10px rgba(0,0,0,0.10)',
        }}
        aria-hidden
      >
        <SafeImage
          src={resolvedLogoUrl}
          alt={method?.title || method?.method || 'Payment method'}
          className="w-full h-full object-contain"
          loading="lazy"
          fallback={<Wallet className="w-6 h-6" style={{ color: accentDark || accent || '#15803D' }} />}
        />
      </span>
    );
  }

  if (code === 'cashapp' || code.includes('cash')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#00D632', boxShadow: '0 4px 10px rgba(0,214,50,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 24 24" width={iconPx} height={iconPx}>
          <path fill="#FFFFFF" d="M19.6 4.4C18.5 3.3 17.1 3 15.7 3H8.3c-1.4 0-2.8.3-3.9 1.4C3.3 5.5 3 6.9 3 8.3v7.4c0 1.4.3 2.8 1.4 3.9 1.1 1.1 2.5 1.4 3.9 1.4h7.4c1.4 0 2.8-.3 3.9-1.4 1.1-1.1 1.4-2.5 1.4-3.9V8.3c0-1.4-.3-2.8-1.4-3.9zM14.2 9.6c-.3-.3-.7-.6-1.4-.7l-.1-1c0-.2-.1-.3-.3-.3h-.7c-.2 0-.3.1-.3.3l-.1.9c-1.4.1-2.5.9-2.5 2.3 0 1.2.8 1.8 2 2.2.9.3 1.4.5 1.4 1 0 .4-.4.7-1.1.7-.7 0-1.4-.3-1.9-.7-.1-.1-.3-.1-.4 0l-.7.7c-.1.1-.1.3 0 .4.5.4 1.2.8 2 1l-.1 1c0 .2.1.3.3.3h.7c.2 0 .3-.1.3-.3l.1-1c1.4-.2 2.4-1 2.4-2.4 0-1.2-.8-1.8-2.2-2.3-.9-.3-1.3-.5-1.3-.9s.4-.6 1-.6c.6 0 1.2.2 1.7.6.1.1.3.1.4 0l.7-.7c.1-.1.1-.3 0-.4z"/>
        </svg>
      </span>
    );
  }

  if (code === 'applepay' || code === 'apple_pay' || code.includes('apple')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#000000', boxShadow: '0 4px 10px rgba(0,0,0,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 60 24" width={iconPx + 14} height={iconPx}>
          <path fill="#FFFFFF" d="M11.55 4.36a3.18 3.18 0 0 0-.74 2.39 3.07 3.07 0 0 0 2.34-1.21 3.36 3.36 0 0 0 .79-2.41 3.4 3.4 0 0 0-2.39 1.23zm2.34 1.85c-1.29-.07-2.39.73-3 .73s-1.56-.7-2.58-.68a3.84 3.84 0 0 0-3.27 2C3.69 11 5 16.05 6.46 18.81c.74 1.36 1.61 2.86 2.76 2.82s1.51-.71 2.84-.71 1.7.71 2.84.69 1.93-1.36 2.65-2.72a11.94 11.94 0 0 0 1.21-2.51 3.92 3.92 0 0 1-2.36-3.57 4 4 0 0 1 1.91-3.34 4.18 4.18 0 0 0-3.27-1.76M27.7 4.7v15.7h2.4v-5.32h3.32c3.05 0 5.18-2.06 5.18-5.2 0-3.13-2.1-5.18-5.1-5.18zM30.1 6.8h2.74c2.06 0 3.24 1.1 3.24 3.06s-1.18 3.08-3.25 3.08H30.1zm12.81 13.83c1.7 0 3.16-.84 3.85-2.18h.05v2.05h2.21v-7.7c0-2.21-1.79-3.65-4.55-3.65-2.56 0-4.46 1.46-4.5 3.46h2.16c.18-1 1.07-1.62 2.27-1.62 1.46 0 2.31.71 2.31 2v.92l-3.07.18c-2.7.16-4.16 1.36-4.16 3.4 0 2.06 1.55 3.14 3.43 3.14zm.65-1.84c-1.27 0-2.07-.61-2.07-1.55 0-.97.78-1.53 2.27-1.62l2.74-.18v.95c0 1.45-1.23 2.4-2.94 2.4zM52.27 24c2.32 0 3.41-.92 4.36-3.69l4.18-12.34h-2.43L55.55 17.6h-.05L52.7 7.97h-2.51l4.05 11.71-.22.71c-.36 1.18-.96 1.65-2.06 1.65-.2 0-.59-.02-.74-.04v1.86c.15.04.78.05.91.05z"/>
        </svg>
      </span>
    );
  }

  if (code === 'googlepay' || code === 'google_pay' || code.includes('google')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
        aria-hidden
      >
        <svg viewBox="0 0 56 24" width={iconPx + 14} height={iconPx}>
          <path fill="#5F6368" d="M26.54 11.66v4.93h-1.56V4.51h4.18a3.78 3.78 0 0 1 2.71 1.06 3.45 3.45 0 0 1 0 5.07 3.81 3.81 0 0 1-2.71 1.02zm0-5.66v4.16h2.66a2.4 2.4 0 0 0 1.79-.71 2.34 2.34 0 0 0 0-3.32 2.4 2.4 0 0 0-1.79-.7zM37 8.14a4.7 4.7 0 0 1 3.32 1.16 3.96 3.96 0 0 1 1.21 3.05v6.16h-1.49v-1.16h-.07c-.65 1-1.78 1.6-2.97 1.56a3.85 3.85 0 0 1-2.66-.94 3.04 3.04 0 0 1-1.05-2.34c0-.99.37-1.78 1.12-2.36a4.66 4.66 0 0 1 2.97-.86 5.15 5.15 0 0 1 2.62.58v-.36a2.05 2.05 0 0 0-.71-1.56 2.45 2.45 0 0 0-1.66-.66 2.6 2.6 0 0 0-2.27 1.21l-1.36-.86A4.04 4.04 0 0 1 37 8.14m-2 6.05c0 .43.21.84.55 1.09.39.31.87.46 1.36.45a2.79 2.79 0 0 0 1.97-.81 2.61 2.61 0 0 0 .87-1.92 3.65 3.65 0 0 0-2.32-.66c-.71 0-1.31.18-1.79.51-.43.32-.65.74-.65 1.34M52 8.41 46.79 20.36h-1.61l1.95-4.2-3.43-7.75h1.7l2.49 6h.04l2.42-6z"/>
          <path fill="#4285F4" d="M19.36 12.65a8.37 8.37 0 0 0-.13-1.5h-7v2.83h4a3.42 3.42 0 0 1-1.49 2.25v1.85h2.4c1.4-1.3 2.22-3.21 2.22-5.43z"/>
          <path fill="#34A853" d="M12.23 19.92c2 0 3.69-.66 4.91-1.79l-2.4-1.85c-.67.45-1.52.71-2.51.71-1.92 0-3.55-1.3-4.13-3.04H5.61v1.91a7.4 7.4 0 0 0 6.62 4.06z"/>
          <path fill="#FBBC04" d="M8.1 13.95a4.43 4.43 0 0 1 0-2.83V9.21H5.61a7.4 7.4 0 0 0 0 6.65z"/>
          <path fill="#EA4335" d="M12.23 8.07a4.01 4.01 0 0 1 2.83 1.11l2.12-2.13a7.13 7.13 0 0 0-4.95-1.93 7.4 7.4 0 0 0-6.62 4.07L8.1 11.1c.58-1.74 2.21-3.04 4.13-3.04z"/>
        </svg>
      </span>
    );
  }

  return (
    <span
      className="rounded-xl flex items-center justify-center shrink-0"
      style={{
        width: dim, height: dim,
        background: `${accent}1A`,
        border: `1px solid ${accent}55`,
      }}
      aria-hidden
    >
      <Wallet style={{ width: iconPx, height: iconPx, color: accentDark }} />
    </span>
  );
}

const gatewayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const gatewaySubmethodCode = (method) => String(
  method?.gateway_submethod
  || method?.method
  || method?.gateway_method
  || method?.gateway_method_code
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const paymentMethodBadgeLabel = (method, mode) => {
  const custom = String(
    method?.player_badge_label
    || method?.player_badge
    || method?.badge_label
    || method?.tag_label
    || method?.display_tag
    || ''
  ).trim();
  if (custom) return custom;
  const bonus = Number(method?.bonus_percent || method?.bonus_or_fee || 0);
  if (mode === 'deposit' && bonus > 0 && (method?.bonus_fee_direction || 'bonus') === 'bonus') {
    return `+${bonus}% BONUS`;
  }
  return '';
};

const paymentBadgeStyle = (tone = 'gold', accent = '#F59E0B') => {
  const styles = {
    gold: {
      color: '#7A4A00',
      borderColor: 'rgba(245, 158, 11, 0.48)',
      background: 'linear-gradient(135deg, rgba(255,237,135,0.98), rgba(245,158,11,0.95))',
      boxShadow: '0 0 14px rgba(245, 158, 11, 0.32)',
    },
    green: {
      color: '#065F46',
      borderColor: 'rgba(16,185,129,0.42)',
      background: 'linear-gradient(135deg, rgba(209,250,229,0.98), rgba(52,211,153,0.95))',
      boxShadow: '0 0 14px rgba(16,185,129,0.28)',
    },
    blue: {
      color: '#075985',
      borderColor: 'rgba(56,189,248,0.42)',
      background: 'linear-gradient(135deg, rgba(224,242,254,0.98), rgba(56,189,248,0.92))',
      boxShadow: '0 0 14px rgba(56,189,248,0.28)',
    },
    purple: {
      color: '#4C1D95',
      borderColor: 'rgba(168,85,247,0.42)',
      background: 'linear-gradient(135deg, rgba(243,232,255,0.98), rgba(168,85,247,0.88))',
      boxShadow: '0 0 14px rgba(168,85,247,0.26)',
    },
    red: {
      color: '#881337',
      borderColor: 'rgba(244,63,94,0.42)',
      background: 'linear-gradient(135deg, rgba(255,228,230,0.98), rgba(244,63,94,0.88))',
      boxShadow: '0 0 14px rgba(244,63,94,0.24)',
    },
  };
  return styles[tone] || {
    color: '#111827',
    borderColor: `${accent}55`,
    background: `linear-gradient(135deg, #FFFFFF, ${accent}33)`,
    boxShadow: `0 0 14px ${accent}28`,
  };
};

const isApplePayGateway = (method) => {
  const values = [
    method?.gateway_submethod,
    method?.method,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
  ];
  return values.some(value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '').includes('applepay'));
};

const isInternalThreeInOne = (method) => {
  const code = String(method?.method || method?.code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return code === '3in1' || code === 'threeinone' || code === 'weblink';
};

const isOneClickPayGateway = (method) => (
  String(method?.gateway_provider || method?.checkout_link_provider_code || '').toLowerCase() === 'oneclickpay'
);

const iframeCheckoutUrl = (payUrl, applePay) => {
  if (!applePay) return payUrl;
  try {
    const parsed = new URL(payUrl);
    if (parsed.hostname === 'checkout.nexmindglobal.com') {
      return `${window.location.origin}/__applepay-checkout${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Fall back to the gateway URL if the browser cannot parse it.
  }
  return payUrl;
};

const amountOptionsFor = (method) => {
  const raw = (
    Array.isArray(method?.allowed_amounts) && method.allowed_amounts.length
      ? method.allowed_amounts
      : Array.isArray(method?.preset_amounts) && method.preset_amounts.length
        ? method.preset_amounts
        : []
  );
  return raw
    .map(v => Number(v))
    .filter(v => Number.isFinite(v) && v > 0)
    .map(v => Math.round(v * 100) / 100)
    .filter((v, idx, arr) => arr.findIndex(x => Math.abs(x - v) < 0.005) === idx)
    .sort((a, b) => a - b);
};

const paymentMethodDisplayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || method?.title
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const defaultPaymentMethodOrder = (method) => {
  const code = paymentMethodDisplayCode(method);
  if (code === 'cashapp') return 10;
  if (code === 'applepay') return 20;
  if (code === 'googlepay') return 30;
  if (code === 'chime') return 40;
  if (code === 'venmo') return 50;
  return 500;
};

const paymentMethodOrder = (method) => {
  const raw = Number(method?.display_order ?? method?.sort_order ?? method?.order);
  return Number.isFinite(raw) ? raw : defaultPaymentMethodOrder(method);
};

const sortPaymentMethodsForPlayer = (items = []) => [...items].sort((a, b) => {
  const orderDiff = paymentMethodOrder(a) - paymentMethodOrder(b);
  if (orderDiff !== 0) return orderDiff;
  const titleA = String(a?.title || a?.method || '').toLowerCase();
  const titleB = String(b?.title || b?.method || '').toLowerCase();
  return titleA.localeCompare(titleB);
});

function ManualPaymentDetails({ method, accent = '#22C55E', accentDark = '#15803D', showQr = true, beforeQr = null }) {
  if (!method) return null;
  const details = method.method_details || '';
  const address = String(method.receiving_address || '').trim();
  const tags = String(method.tags || '').trim();
  const note = method.admin_note || '';
  const qr = method.qr_image_url || method.qr_code_url || '';
  const walletName = address || 'Wallet tag name';
  const walletTag = tags || address;
  const noteLines = [details, note].filter(Boolean);
  const supportOnlyMethod = [
    method.title,
    method.method,
    method.player_badge_label,
    details,
    address,
    tags,
    note,
  ].some((value) => String(value || '').toLowerCase().includes('contact support'));
  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (!details && !address && !tags && !note && !(showQr && qr)) return null;

  return (
    <div className="space-y-3">
      {walletTag && (
        <div
          className="rounded-2xl p-4"
          style={{ background: `${accent}10`, border: `1px solid ${accent}55` }}
          data-testid="manual-wallet-tag-card"
        >
          <p className="text-xs font-extrabold break-all" style={{ color: '#0B0F0C' }}>
            {walletName}
          </p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xl font-extrabold mono break-all leading-tight" style={{ color: '#0B0F0C' }}>
              {walletTag}
            </p>
            <button
              type="button"
              onClick={() => copyText(walletTag)}
              className="shrink-0 p-2.5 rounded-xl"
              style={{ background: accent, color: '#fff' }}
              aria-label={`Copy ${walletName} tag`}
              data-testid="copy-manual-wallet-tag"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {!supportOnlyMethod && (
            <p className="mt-2 flex items-start gap-1.5 text-[10px] sm:text-[11px] font-bold leading-snug" style={{ color: '#DC2626' }}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Make sure to double check the tag before sending payment. Payment won&apos;t be loaded if you send on another tag.</span>
            </p>
          )}
        </div>
      )}
      {beforeQr}
      {showQr && qr && (
        <div className="flex justify-center">
          <SafeImage src={qr} alt={`${method.title || 'Payment'} QR`} className="w-44 h-44 rounded-xl bg-white object-contain" style={{ border: '1px solid rgba(0,0,0,0.1)' }} loading="lazy" fallback={null} />
        </div>
      )}
      {noteLines.length > 0 && (
        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.35)', color: '#92400E' }}>
          {noteLines.map((line, index) => (
            <p key={`${line}-${index}`} className={index ? 'mt-2' : ''}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

const isSupportContactMethod = (method) => {
  if (!method) return false;
  if ((method.payment_mode || '').toUpperCase() === 'GATEWAY') return false;
  const hasWalletDetails = String(method.receiving_address || method.tags || '').trim().length > 0;
  const hasQr = String(method.qr_image_url || method.qr_code_url || '').trim().length > 0;
  return !hasWalletDetails && !hasQr;
};

/* -------------------- WALLET FLOW (full-screen, multi-step) -------------------- */
export default function WalletFlow({ mode, setMode, step, setStep, method, setMethod, wallet, walletRules = {}, methods, refresh, checkAuth, close, branding = {}, onOpenSupport }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [tipAgent, setTipAgent] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [checkoutPanel, setCheckoutPanel] = useState(null);
  const [latestSelectedMethod, setLatestSelectedMethod] = useState(null);
  const proofInputRef = useRef(null);
  // setMode is reserved by parent — referenced here so eslint doesn't trip
  void setMode;

  const normalizedMode = mode === 'withdrawal' ? 'withdraw' : mode;
  const sortedMethods = useMemo(() => sortPaymentMethodsForPlayer(methods), [methods]);
  const filteredMethods = sortedMethods.filter(m => {
    const flow = String(m?.flow || '').toLowerCase();
    return (flow === 'both' || flow === normalizedMode) && !isInternalThreeInOne(m);
  });
  const activeMethod = latestSelectedMethod
    && method?.id
    && String(latestSelectedMethod.id) === String(method.id)
    ? latestSelectedMethod
    : method;
  const accent = mode === 'deposit' ? '#22C55E' : '#F97316';
  const accentDark = mode === 'deposit' ? '#15803D' : '#C2410C';
  const balanceUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const withdrawalRules = walletRules?.withdrawal_rules || {};
  const maxWithdrawalPerDay = Number(
    withdrawalRules.max_total_per_day ??
    withdrawalRules.max_daily_withdraw_total ??
    0
  ) || 0;
  const enteredAmount = Number(amount);
  const parsedTipAmount = Number(tipAmount);
  const enteredTipAmount = mode === 'withdrawal' && Number.isFinite(parsedTipAmount) && parsedTipAmount > 0
    ? Math.round(parsedTipAmount * 100) / 100
    : 0;
  const cashoutTipAmount = tipAgent ? enteredTipAmount : 0;
  const cashoutGrossAmount = mode === 'withdrawal' && Number.isFinite(enteredAmount) && enteredAmount > 0
    ? Math.round(enteredAmount * 100) / 100
    : 0;
  const cashoutNetAmount = mode === 'withdrawal'
    ? Math.max(Math.round((cashoutGrossAmount - cashoutTipAmount) * 100) / 100, 0)
    : cashoutGrossAmount;
  const hasTip = cashoutTipAmount > 0 && !!tipAgent;
  const exceedsDailyWithdrawalCap = mode === 'withdrawal'
    && maxWithdrawalPerDay > 0
    && cashoutGrossAmount > maxWithdrawalPerDay;
  const tipAgents = (Array.isArray(branding?.chat_agents) ? branding.chat_agents : [])
    .filter(agent => agent && agent.enabled !== false && (agent.name || agent.id));
  const resolveMediaUrl = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) return value;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${value}`;
  };
  const brandLogoUrl = (() => {
    const raw = String(branding?.logo_url || branding?.intro_logo_url || '').trim();
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`;
  })();

  const requestClose = () => {
    if (mode === 'deposit' && step === 2) {
      setStep(1);
      return;
    }
    if (step >= 2) {
      setConfirmCloseOpen(true);
    } else {
      close();
    }
  };

  const openSupportForLoad = () => {
    if (typeof onOpenSupport === 'function') {
      onOpenSupport();
      return;
    }
    toast.message('Open support from the chat button and our agents will help you load.');
  };

  useEffect(() => {
    if (mode !== 'withdrawal') {
      setTipAgent(null);
      setTipAmount('');
    }
  }, [mode]);

  useEffect(() => {
    if (method?.id && !filteredMethods.some(m => m.id === method.id)) {
      setMethod(null);
    }
  }, [filteredMethods, method?.id, setMethod]);

  useEffect(() => {
    if (!method?.id) {
      setLatestSelectedMethod(null);
      return undefined;
    }
    let cancelled = false;
    setLatestSelectedMethod(null);
    const flow = normalizedMode === 'withdraw' ? 'withdraw' : 'deposit';
    api.get(`/api/payment-methods/portal/${flow}`)
      .then(res => {
        if (cancelled) return;
        const list = res.data?.payment_methods || res.data?.methods || [];
        const fresh = list.find(item => String(item?.id) === String(method.id));
        if (fresh) {
          setLatestSelectedMethod({ ...method, ...fresh });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [method, method?.id, normalizedMode, step]);

  const stepperLabels = mode === 'withdrawal'
    ? ['Amount & Tip', 'Cashout Method', 'Transaction Details', 'Confirmation']
    : ['Payment method', 'Pay'];

  const submit = async () => {
    const selectedAmount = mode === 'withdrawal' ? cashoutNetAmount : Number(amount);
    if (mode === 'withdrawal') {
      if (!cashoutGrossAmount || cashoutGrossAmount <= 0) { toast.error('Enter cashout amount'); return; }
      if (exceedsDailyWithdrawalCap) { toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`); return; }
      if (cashoutTipAmount >= cashoutGrossAmount) { toast.error('Tip must be lower than the cashout amount'); return; }
      if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) { toast.error('Withdrawal amount must be greater than $0'); return; }
    } else if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) {
      toast.error('Select a load amount');
      return;
    }
    if (!method) { toast.error('Pick a payment method'); return; }
    setProcessing(true);
    try {
      if (mode === 'deposit') {
        const isGateway = (method.payment_mode || '').toUpperCase() === 'GATEWAY';
        if (!isGateway && !proofFile) {
          toast.error('Upload payment screenshot before submitting');
          setProcessing(false);
          return;
        }
        if (isGateway) {
          const oneClickPay = isOneClickPayGateway(method);
          const shouldOpenDirect = isApplePayGateway(method) && !oneClickPay;
          const shouldTreatAsApplePay = isApplePayGateway(method);
          const idem = `gw-deposit-${user?.id || 'anon'}-${Date.now()}`;
          const gatewayMethod = gatewayCode(method);
          const gatewaySubmethod = gatewaySubmethodCode(method);
          if (!shouldOpenDirect) {
            setCheckoutPanel({
              loading: true,
              url: '',
              originalUrl: '',
              orderId: null,
              amount: selectedAmount,
              methodTitle: method.title,
              applePay: shouldTreatAsApplePay,
            });
          }
          const res = await api.post(
            '/api/gateway/deposit',
            {
              amount: selectedAmount,
              method: gatewayMethod,
              currency: 'USD',
              gateway_submethod: gatewaySubmethod,
            },
            { headers: { 'X-Idempotency-Key': idem } }
          );
          const payUrl = res.data?.pay_url;
          if (!payUrl) {
            const message = 'Gateway did not return a payment URL';
            toast.error(message);
            setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
            setProcessing(false);
            return;
          }
          if (shouldOpenDirect) {
            toast.success('Opening Apple Pay checkout…');
            window.location.assign(payUrl);
            return;
          }
          toast.success('Opening secure checkout…');
          setCheckoutPanel(prev => ({
            ...(prev || {}),
            loading: false,
            linkReady: false,
            url: iframeCheckoutUrl(payUrl, shouldTreatAsApplePay),
            originalUrl: payUrl,
            orderId: res.data?.order_id,
            amount: res.data?.charged_amount || selectedAmount,
            methodTitle: method.title,
            applePay: shouldTreatAsApplePay,
          }));
          return;
        }
        const res = await api.post('/api/requests/deposit', { amount: selectedAmount, payment_method_id: method.id });
        const txnId = res.data.id;
        if (proofFile && txnId) {
          const fd = new FormData();
          fd.append('file', proofFile);
          await api.post(`/api/requests/deposit/${txnId}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        toast.success('Purchase request submitted!');
      } else {
        if (!walletAddress && !payoutDetails) { toast.error('Enter payout details'); setProcessing(false); return; }
        const normalizedPayoutDetails = {};
        if (walletAddress) {
          normalizedPayoutDetails.address = walletAddress;
          normalizedPayoutDetails.wallet_address = walletAddress;
          normalizedPayoutDetails.tag = walletAddress;
        }
        if (payoutDetails) {
          normalizedPayoutDetails.details = payoutDetails;
          normalizedPayoutDetails.note = payoutDetails;
        }
        await api.post('/api/requests/withdraw', {
          amount: selectedAmount, payment_method_id: method.id,
          payout_details: normalizedPayoutDetails,
          tip_amount: hasTip ? cashoutTipAmount : 0,
          tip_agent_id: hasTip ? (tipAgent?.id || '') : '',
          tip_agent_name: hasTip ? (tipAgent?.name || '') : '',
          tip_agent_title: hasTip ? (tipAgent?.title || '') : '',
          tip_agent_avatar_url: hasTip ? (tipAgent?.avatar_url || '') : '',
        });
        toast.success('Cashout request submitted!');
        await checkAuth();
      }
      refresh();
      close();
    } catch (err) {
      const message = err.response?.data?.detail || 'Request failed';
      toast.error(message);
      if ((method?.payment_mode || '').toUpperCase() === 'GATEWAY') {
        setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
      }
    } finally { setProcessing(false); }
  };

  useEffect(() => {
    if (!checkoutPanel?.orderId) return undefined;
    let stopped = false;
    let tries = 0;

    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const res = await api.post(`/api/deposits/${checkoutPanel.orderId}/poll-status`);
        const status = String(res.data?.status || res.data?.gateway_status || '').toUpperCase();
        if (['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(status)) {
          toast.success('Payment verified. Wallet updated.');
          await checkAuth?.();
          refresh?.();
          setCheckoutPanel(prev => (prev ? { ...prev, verified: true } : prev));
          return;
        }
        if (status === 'FAILED') {
          toast.error('Payment was not completed.');
          setCheckoutPanel(prev => (prev ? { ...prev, failed: true } : prev));
          return;
        }
      } catch {
        // Keep polling; iframe providers can take a few seconds to settle.
      }
      if (!stopped && tries < 90) {
        window.setTimeout(poll, tries < 5 ? 2500 : 5000);
      }
    };

    const timer = window.setTimeout(poll, 2500);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [checkoutPanel, checkAuth, close, refresh]);

  const moneyLabel = (value) => `$${Number(value || 0).toFixed(2)}`;
  const validateWithdrawalAmountStep = () => {
    if (!cashoutGrossAmount || cashoutGrossAmount <= 0) {
      toast.error('Confirm your cashout amount');
      return false;
    }
    if (exceedsDailyWithdrawalCap) {
      toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`);
      return false;
    }
    if (cashoutTipAmount >= cashoutGrossAmount) {
      toast.error('Tip must be lower than the cashout amount');
      return false;
    }
    if (cashoutNetAmount <= 0) {
      toast.error('Withdrawal amount must be greater than $0');
      return false;
    }
    return true;
  };

  const renderMethodList = (intro = 'Pick a payment method to continue.') => (
    <div className="space-y-3" data-testid="wallet-step-method">
      <p className="text-sm" style={{ color: '#6B7280' }}>{intro}</p>
      {filteredMethods.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: '#F3F4F6' }}>
          <p className="font-bold" style={{ color: '#0B0F0C' }}>No methods available</p>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
        </div>
      ) : filteredMethods.map(m => {
        const selected = method?.id === m.id;
        const badgeLabel = paymentMethodBadgeLabel(m, mode);
        const badgeStyle = paymentBadgeStyle(m.player_badge_tone, accent);
        return (
          <button
            key={m.id}
            onClick={() => setMethod(m)}
            className="w-full p-4 rounded-2xl flex items-center gap-4 transition active:scale-[0.99] relative overflow-hidden"
            style={{
              background: selected ? `${accent}10` : '#F8FAFC',
              border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
              boxShadow: selected ? `0 6px 18px ${accent}33` : 'none'
            }}
            data-testid={`fs-pay-method-${m.id}`}
          >
            <PaymentMethodLogo method={m} accent={accent} accentDark={accentDark} />
            <div className="flex-1 text-left">
              <p className="display text-base font-bold" style={{ color: '#0B0F0C' }}>{m.title}</p>
              <div className="flex gap-2 mt-0.5 flex-wrap">
                {m.bonus_percent > 0 && mode === 'deposit' && (
                  <span className="text-[11px] font-bold" style={{ color: '#15803D' }}>+{m.bonus_percent}% bonus</span>
                )}
                {m.min_amount && (<span className="text-[11px]" style={{ color: '#6B7280' }}>Min ${m.min_amount}</span>)}
              </div>
            </div>
            <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
              {badgeLabel && (
                <span
                  className="payment-method-hot-badge"
                  style={badgeStyle}
                >
                  <Sparkles className="h-3 w-3" />
                  {badgeLabel}
                </span>
              )}
              {selected ? (
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
                  <Check className="w-4 h-4 text-white" strokeWidth={4} />
                </div>
              ) : (
                <ChevronDown className="w-4 h-4 -rotate-90" style={{ color: '#9CA3AF' }} />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderWithdrawalAmountStep = () => {
    const quickAmounts = [10, 25, 50, 100, 250];
    const tipOptions = [5, 10, 25, 50];
    return (
      <div className="space-y-5" data-testid="wallet-step-withdrawal-amount">
        <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Amount to withdraw</p>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Confirm the wallet amount first, then add an optional agent tip below.</p>
          {maxWithdrawalPerDay > 0 && (
            <div
              className="mt-3 rounded-xl px-3 py-2 text-xs font-extrabold"
              style={{
                background: exceedsDailyWithdrawalCap ? '#FEF2F2' : '#FFFBEB',
                border: `1px solid ${exceedsDailyWithdrawalCap ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
                color: exceedsDailyWithdrawalCap ? '#991B1B' : '#92400E',
              }}
              data-testid="withdrawal-daily-cap"
            >
              Max withdrawal per day: {moneyLabel(maxWithdrawalPerDay)}
            </div>
          )}
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0.00"
            className="h-14 text-2xl font-bold mono mt-4"
            style={{ background: '#FFFFFF', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
            data-testid="fs-wallet-amount"
            autoFocus
          />
          <div className="grid grid-cols-5 gap-2 mt-3">
            {quickAmounts.map(v => {
              const selected = Number(amount) === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(String(v))}
                  className="py-2 rounded-xl text-sm font-bold display transition"
                  style={{
                    background: selected ? accent : '#F3F4F6',
                    color: selected ? '#FFFFFF' : '#0B0F0C',
                  }}
                >
                  ${v}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }}>
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Optional tip to your favorite agent</p>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Optional. Pick an agent first, then choose a tip.</p>
          </div>

          {tipAgents.length === 0 ? (
            <div className="rounded-xl p-4 text-sm" style={{ background: '#FFFFFF', color: '#6B7280', border: '1px dashed rgba(0,0,0,0.14)' }}>
              No agents available for tips right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tipAgents.map(agent => {
                const selected = String(tipAgent?.id || tipAgent?.name) === String(agent.id || agent.name);
                const avatar = resolveMediaUrl(agent.avatar_url);
                return (
                  <button
                    key={agent.id || agent.name}
                    type="button"
                    onClick={() => {
                      setTipAgent(agent);
                      if (!tipAmount) setTipAmount('5');
                    }}
                    className="p-3 rounded-2xl flex items-center gap-3 text-left transition active:scale-[0.99]"
                    style={{
                      background: selected ? `${accent}12` : '#FFFFFF',
                      border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: selected ? `0 8px 18px ${accent}25` : 'none',
                    }}
                  >
                    <span className="w-12 h-12 rounded-full overflow-hidden shrink-0 flex items-center justify-center" style={{ background: `${accent}18`, color: accentDark }}>
                      {avatar ? (
                        <SafeImage
                          src={avatar}
                          alt={agent.name || 'Agent'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          fallback={<UserRound className="w-6 h-6" />}
                        />
                      ) : (
                        <UserRound className="w-6 h-6" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="display font-extrabold block truncate" style={{ color: '#0B0F0C' }}>{agent.name || 'Agent'}</span>
                      <span className="text-xs block truncate" style={{ color: '#6B7280' }}>{agent.title || 'Support Agent'}</span>
                    </span>
                    {selected && <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: accentDark }} />}
                  </button>
                );
              })}
            </div>
          )}

          {tipAgent && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {tipOptions.map(v => {
                  const selected = Number(tipAmount) === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTipAmount(String(v))}
                      className="py-2 rounded-xl text-sm font-bold display transition"
                      style={{
                        background: selected ? accent : '#EEF2F7',
                        color: selected ? '#FFFFFF' : '#0B0F0C',
                      }}
                    >
                      ${v}
                    </button>
                  );
                })}
              </div>
              <Input
                type="number"
                min="0"
                value={tipAmount}
                onChange={e => setTipAmount(e.target.value)}
                placeholder="Custom tip amount"
                className="h-12"
                style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
                data-testid="wallet-tip-amount"
              />
              <button
                type="button"
                onClick={() => { setTipAgent(null); setTipAmount(''); }}
                className="text-xs display font-bold"
                style={{ color: '#6B7280' }}
              >
                Remove tip
              </button>
            </div>
          )}
        </div>

        <div className="rounded-2xl p-5 space-y-2 text-sm" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
          {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
          <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
          {maxWithdrawalPerDay > 0 && <Row label="Daily max" value={moneyLabel(maxWithdrawalPerDay)} valueBold={exceedsDailyWithdrawalCap} />}
        </div>
      </div>
    );
  };

  const renderDepositAmountStep = () => {
    const displayMethod = activeMethod || method;
    const supportContactMethod = isSupportContactMethod(displayMethod);
    return (
      <div className="space-y-5" data-testid="wallet-step-amount">
        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }}>
          <PaymentMethodLogo method={displayMethod} accent={accent} accentDark={accentDark} size="sm" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Method</p>
            <p className="display font-bold" style={{ color: '#0B0F0C' }}>{displayMethod.title}</p>
          </div>
          <button onClick={() => setStep(1)} className="text-xs font-bold display" style={{ color: accentDark }}>Change</button>
        </div>

        {supportContactMethod ? (
          <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.35)' }}>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-extrabold" style={{ color: '#15803D' }}>
                Contact support for load
              </p>
              <p className="display text-xl font-extrabold mt-2" style={{ color: '#0B0F0C' }}>
                Our agent will help you complete this load.
              </p>
            </div>
            <ManualPaymentDetails method={displayMethod} accent={accent} accentDark={accentDark} showQr={false} />
            <button
              type="button"
              onClick={openSupportForLoad}
              className="w-full h-12 rounded-xl display font-extrabold"
              style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
              data-testid="wallet-open-support-load"
            >
              Open Support →
            </button>
          </div>
        ) : (() => {
          const isGateway = (displayMethod?.payment_mode || '').toUpperCase() === 'GATEWAY';
          const quickAmounts = amountOptionsFor(displayMethod);
          const showCustomInput = !isGateway;
          return (
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block font-bold" style={{ color: '#6B7280' }}>
                {isGateway && quickAmounts.length ? 'Select Load Amount' : 'Amount ($)'}
              </label>
              {showCustomInput && (
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-14 text-2xl font-bold mono"
                  style={{ background: '#fff', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
                  data-testid="fs-wallet-amount"
                  autoFocus
                />
              )}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                {(quickAmounts.length ? quickAmounts : (isGateway ? [] : [5, 10, 25, 50, 100])).map(v => {
                  const display = Number.isInteger(Number(v)) ? `$${v}` : `$${Number(v).toFixed(2)}`;
                  const valStr = String(v);
                  const selected = Number(amount) === Number(v);
                  return (
                    <button
                      key={valStr}
                      onClick={() => setAmount(valStr)}
                      disabled={processing}
                      className="py-2 rounded-xl text-sm font-bold display transition disabled:opacity-60"
                      style={{
                        background: selected ? accent : '#F3F4F6',
                        color: selected ? '#fff' : '#0B0F0C'
                      }}
                      data-testid={`quick-${valStr}`}
                    >
                      {processing && selected ? '...' : display}
                    </button>
                  );
                })}
              </div>
              {isGateway && quickAmounts.length > 0 && (
                <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
                  Select an amount, then continue to checkout.
                </p>
              )}
              {isGateway && quickAmounts.length === 0 && (
                <div className="rounded-2xl p-4 text-center" style={{ background: '#F3F4F6' }}>
                  <p className="font-bold" style={{ color: '#0B0F0C' }}>No load amounts configured</p>
                  <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
                </div>
              )}
            </div>
          );
        })()}

        {mode === 'deposit' && displayMethod && !supportContactMethod && (() => {
          const isGateway = (displayMethod.payment_mode || '').toUpperCase() === 'GATEWAY';
          if (isGateway) {
            return (
              <div
                className="rounded-2xl p-4 flex items-start gap-3"
                style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.4)' }}
                data-testid="gateway-instant-notice"
              >
                <span
                  className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                  style={{ background: '#22C55E', boxShadow: '0 0 12px rgba(34,197,94,0.55)' }}
                >
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                <div className="text-sm leading-snug" style={{ color: '#0F2D1A' }}>
                  <p className="font-extrabold display">Instant secure checkout</p>
                  <p className="mt-1" style={{ color: '#3F6D52' }}>
                    You&apos;ll be sent to the secure {displayMethod.title} checkout page. Your wallet
                    is credited automatically the moment payment is verified — no screenshot needed.
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              <ManualPaymentDetails
                method={displayMethod}
                accent={accent}
                accentDark={accentDark}
                showQr
                beforeQr={(
                  <>
                    <input type="file" ref={proofInputRef} accept="image/*" className="hidden" onChange={e => setProofFile(e.target.files[0])} />
                    <div onClick={() => proofInputRef.current?.click()} className="border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer" style={{ borderColor: 'rgba(34,197,94,0.4)', background: '#F0FDF4' }}>
                      {proofFile ? (
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
                          <span className="font-bold" style={{ color: '#0B0F0C' }}>{proofFile.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setProofFile(null); }}><XCircle className="w-4 h-4" style={{ color: '#9CA3AF' }} /></button>
                        </div>
                      ) : (
                        <p className="text-sm font-bold" style={{ color: '#15803D' }}>Click to upload payment screenshot (required)</p>
                      )}
                    </div>
                  </>
                )}
              />
            </div>
          );
        })()}
      </div>
    );
  };

  const renderSelectedCashoutMethod = () => {
    if (!method) return null;
    return (
      <div
        className="w-full p-4 rounded-2xl flex items-center gap-4"
        style={{
          background: `${accent}08`,
          border: `2px solid ${accent}`,
          boxShadow: `0 8px 22px ${accent}22`,
        }}
        data-testid="wallet-selected-cashout-method"
      >
        <PaymentMethodLogo method={method} accent={accent} accentDark={accentDark} />
        <div className="flex-1 text-left">
          <p className="display text-base font-bold" style={{ color: '#0B0F0C' }}>{method.title}</p>
          {method.min_amount && (<p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Min ${method.min_amount}</p>)}
        </div>
        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
          <Check className="w-4 h-4 text-white" strokeWidth={4} />
        </div>
      </div>
    );
  };

  const renderWithdrawalSummary = () => (
    <div className="rounded-2xl p-4 text-sm space-y-2" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
      <Row label="Confirmed amount" value={moneyLabel(cashoutGrossAmount)} valueBold />
      {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
      <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
    </div>
  );

  const renderWithdrawalMethodStep = () => (
    <div className="space-y-5" data-testid="wallet-step-withdrawal-method">
      {renderWithdrawalSummary()}
      {renderMethodList('Pick a cashout method to continue.')}
    </div>
  );

  const renderWithdrawalDetailsStep = () => (
    <div className="space-y-5" data-testid="wallet-step-withdrawal-details">
      {renderWithdrawalSummary()}
      {method && (
        <div className="space-y-3">
          {renderSelectedCashoutMethod()}
          <ManualPaymentDetails method={method} accent={accent} accentDark={accentDark} showQr />
          <Input
            value={walletAddress}
            onChange={e => setWalletAddress(e.target.value)}
            placeholder={`Your ${method.title} address / username`}
            className="h-12"
            style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
            data-testid="fs-wallet-address"
          />
          <Input
            value={payoutDetails}
            onChange={e => setPayoutDetails(e.target.value)}
            placeholder="Additional details (optional)"
            className="h-12"
            style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
          />
        </div>
      )}
    </div>
  );

  const renderWithdrawalReviewStep = () => (
    <div className="space-y-5" data-testid="wallet-step-confirm">
      <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
        <p className="display font-extrabold text-lg mb-3" style={{ color: '#0B0F0C' }}>
          Review your cashout
        </p>
        <div className="space-y-2.5 text-sm">
          <Row label="Method" value={method?.title || '-'} />
          <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
          {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
          <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
          {walletAddress && <Row label="Address / Username" value={walletAddress} mono />}
          {payoutDetails && <Row label="Details" value={payoutDetails} />}
          <Row label="Current Balance" value={moneyLabel(balanceUsd)} />
        </div>
      </div>

      <div className="rounded-2xl p-4 text-sm" style={{ background: '#FEF3C7', border: '1px solid rgba(180,83,9,0.25)', color: '#92400E' }}>
        Once submitted, your cashout request will be processed by our team. You can track it in your transaction history.
      </div>
    </div>
  );

  if (checkoutPanel) {
    return (
      <div
        className="fixed inset-0 z-[70] flex flex-col"
        style={{ background: '#FFFFFF', color: '#0B0F0C', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        data-testid="embedded-checkout-panel"
      >
        <div className="shrink-0 px-4 sm:px-6 py-3 flex items-center gap-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setCheckoutPanel(null)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label="Back to amount selection"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--vl-brand-primary, #D4AF37)' }}>Secure Checkout</p>
            <p className="display font-extrabold truncate" style={{ color: '#0B0F0C' }}>
              {checkoutPanel.methodTitle} · ${Number(checkoutPanel.amount || 0).toFixed(2)}
            </p>
          </div>
          <button
            onClick={close}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label="Close checkout"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          className="px-4 py-2 text-xs text-center"
          style={{
            background: checkoutPanel.failed ? '#FEF2F2' : checkoutPanel.verified ? '#F0FDF4' : 'rgba(255,217,61,0.16)',
            color: checkoutPanel.failed ? '#991B1B' : checkoutPanel.verified ? '#166534' : '#6B4800'
          }}
        >
          {checkoutPanel.verified
            ? 'Payment verified. Your wallet has been updated.'
            : checkoutPanel.failed
              ? 'Payment was not completed. You can go back and try again.'
              : 'Keep this panel open after payment. Your wallet updates automatically when payment is verified.'}
        </div>
        {checkoutPanel.loading ? (
          <div className="flex-1 flex items-center justify-center px-6 py-10">
            <div className="w-full max-w-sm text-center">
              <style>{`
                @keyframes checkout-gold-spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes checkout-gold-pulse {
                  0%, 100% { opacity: 0.28; transform: scale(0.82); }
                  50% { opacity: 0.75; transform: scale(1.18); }
                }
                @keyframes checkout-gold-sweep {
                  0% { transform: rotate(0deg) translateX(34px) rotate(0deg); opacity: 0.15; }
                  50% { opacity: 1; }
                  100% { transform: rotate(360deg) translateX(34px) rotate(-360deg); opacity: 0.15; }
                }
              `}</style>
              <div className="relative w-28 h-28 mx-auto flex items-center justify-center" aria-label="Creating checkout">
                <span
                  className="absolute inset-2 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(255,217,61,0.42) 0%, rgba(255,217,61,0.14) 48%, rgba(255,217,61,0) 72%)',
                    boxShadow: '0 0 42px rgba(255,217,61,0.55)',
                    animation: 'checkout-gold-pulse 1.7s ease-in-out infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-24 h-24 rounded-full"
                  style={{
                    border: '2px solid rgba(212,175,55,0.24)',
                    borderTopColor: 'var(--vl-brand-primary, #D4AF37)',
                    borderRightColor: '#FFD93D',
                    filter: 'drop-shadow(0 0 12px rgba(255,217,61,0.7))',
                    animation: 'checkout-gold-spin 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-4 h-4 rounded-full"
                  style={{
                    background: '#FFD93D',
                    boxShadow: '0 0 18px rgba(255,217,61,0.95), 0 0 34px rgba(212,175,55,0.75)',
                    animation: 'checkout-gold-sweep 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <div
                  className="relative w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    background: brandLogoUrl ? '#FFFFFF' : 'linear-gradient(180deg, #FFF3A3 0%, #FFD93D 45%, var(--vl-brand-primary, #D4AF37) 100%)',
                    color: brandLogoUrl ? '#0B0F0C' : '#2B1E00',
                    border: '2px solid rgba(255,255,255,0.72)',
                    boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.55), 0 14px 34px rgba(212,175,55,0.42), 0 0 0 5px rgba(255,217,61,0.18)',
                  }}
                >
                  {brandLogoUrl ? (
                    <SafeImage
                      src={brandLogoUrl}
                      alt={branding?.brand_name || 'Brand logo'}
                      className="w-full h-full object-cover"
                      data-testid="checkout-loader-brand-logo"
                      loading="eager"
                      fallback={<RefreshCw className="w-8 h-8 animate-spin" />}
                    />
                  ) : (
                    <RefreshCw
                      className="w-8 h-8"
                      strokeWidth={3}
                      style={{ animation: 'checkout-gold-spin 1.05s linear infinite' }}
                    />
                  )}
                  <span
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.42), transparent 36%)',
                      boxShadow: 'inset 0 0 16px rgba(43,30,0,0.18)',
                    }}
                    aria-hidden
                  />
                </div>
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                Creating secure checkout
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                Hold on. We are generating your {checkoutPanel.methodTitle} payment screen now.
              </p>
            </div>
          </div>
        ) : checkoutPanel.verified || checkoutPanel.failed ? (
          <div className="flex-1 flex items-center justify-center px-6 py-10">
            <div className="w-full max-w-sm text-center">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: checkoutPanel.verified ? '#DCFCE7' : '#FEE2E2',
                  color: checkoutPanel.verified ? '#16A34A' : '#DC2626'
                }}
              >
                {checkoutPanel.verified ? (
                  <CheckCircle2 className="w-11 h-11" strokeWidth={3} />
                ) : (
                  <XCircle className="w-11 h-11" strokeWidth={3} />
                )}
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                {checkoutPanel.verified ? 'Payment verified' : 'Payment not completed'}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                {checkoutPanel.verified
                  ? `Your $${Number(checkoutPanel.amount || 0).toFixed(2)} ${checkoutPanel.methodTitle} deposit has been credited.`
                  : (checkoutPanel.errorMessage || 'No wallet credit was added for this attempt. Go back and choose an amount to try again.')}
              </p>
              {checkoutPanel.failed && (
                <button
                  onClick={() => setCheckoutPanel(null)}
                  className="mt-6 w-full h-12 rounded-xl display font-extrabold"
                  style={{ background: '#F3F4F6', color: '#0B0F0C' }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        ) : (
          <iframe
            title="Secure payment checkout"
            src={checkoutPanel.url}
            className="flex-1 w-full border-0"
            allow={checkoutPanel.applePay ? 'payment; publickey-credentials-get; clipboard-read; clipboard-write' : 'payment; clipboard-read; clipboard-write'}
            allowPaymentRequest
            {...(checkoutPanel.applePay ? {} : {
              sandbox: 'allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-storage-access-by-user-activation'
            })}
          />
        )}
        <div className="shrink-0 px-4 sm:px-6 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)', background: '#FFFFFF' }}>
          <button
            onClick={close}
            className="w-full h-12 rounded-xl display font-extrabold flex items-center justify-center gap-2"
            style={{ background: '#0B0F0C', color: '#FFFFFF' }}
          >
            <ChevronLeft className="w-4 h-4" /> Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={requestClose}
        className="fixed top-4 right-4 z-[70] w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: '2px solid rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)'
        }}
        data-testid="wallet-close"
        aria-label="Close"
      >
        <X className="w-5 h-5 text-white" strokeWidth={3} />
      </button>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10 sm:py-12">
          <Stepper step={step} accent={accent} accentDark={accentDark} labels={stepperLabels} />

          <div className="flex justify-start mt-8 mb-5">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{
                background: mode === 'deposit' ? '#F0FDF4' : '#FFF7ED',
                border: `1px solid ${accent}55`
              }}
              data-testid="wallet-current-balance"
            >
              <Wallet className="w-4 h-4" style={{ color: accentDark }} />
              <span className="text-xs uppercase tracking-wider font-bold" style={{ color: accentDark }}>Current Balance:</span>
              <span className="display font-extrabold mono" style={{ color: accentDark }}>${balanceUsd.toFixed(2)}</span>
            </div>
          </div>

          {mode === 'withdrawal' ? (
            step === 1
              ? renderWithdrawalAmountStep()
              : step === 2
                ? renderWithdrawalMethodStep()
                : step === 3
                  ? renderWithdrawalDetailsStep()
                  : renderWithdrawalReviewStep()
          ) : (
            step === 1 ? renderMethodList() : renderDepositAmountStep()
          )}
        </div>
      </div>

      <div
        className="shrink-0 border-t"
        style={{
          background: '#FFFFFF',
          borderColor: 'rgba(0,0,0,0.08)',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.05)'
        }}
        data-testid="wallet-footer"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-4 space-y-2">
          {step === 1 && (
            <Button
              onClick={() => {
                if (mode === 'withdrawal') {
                  if (validateWithdrawalAmountStep()) setStep(2);
                  return;
                }
                if (method) setStep(2);
                else toast.error('Select a payment method');
              }}
              disabled={mode === 'withdrawal' ? (!cashoutGrossAmount || exceedsDailyWithdrawalCap) : !method}
              className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                color: '#fff',
                boxShadow: (mode === 'withdrawal' ? (cashoutGrossAmount && !exceedsDailyWithdrawalCap) : method) ? `0 8px 22px ${accent}66` : 'none'
              }}
              data-testid="wallet-next-btn"
            >
              Next →
            </Button>
          )}

          {step === 2 && mode === 'deposit' && (
            <>
              <Button
                onClick={() => {
                  if (isSupportContactMethod(activeMethod || method)) {
                    openSupportForLoad();
                    return;
                  }
                  submit();
                }}
                disabled={processing || (!isSupportContactMethod(activeMethod || method) && (!amount || (((method?.payment_mode || '').toUpperCase() !== 'GATEWAY') && !proofFile)))}
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#22C55E,#15803D)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(34,197,94,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  isSupportContactMethod(activeMethod || method)
                    ? 'Open Support →'
                    : (method && (method.payment_mode || '').toUpperCase() === 'GATEWAY')
                    ? 'Continue to Secure Checkout →'
                    : 'Confirm Purchase'
                )}
              </Button>
              <button
                onClick={close}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Home
              </button>
            </>
          )}

          {step === 2 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  if (!method) { toast.error('Select a cashout method'); return; }
                  setStep(3);
                }}
                disabled={!method}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: method ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step3-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(1)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to amount
              </button>
            </>
          )}

          {step === 3 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  if (!walletAddress && !payoutDetails) { toast.error('Enter payout details'); return; }
                  setStep(4);
                }}
                disabled={!walletAddress && !payoutDetails}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: (walletAddress || payoutDetails) ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step4-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(2)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to cashout method
              </button>
            </>
          )}

          {step === 4 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => submit()}
                disabled={processing}
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#F97316,#C2410C)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(249,115,22,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing ? <div className="spinner w-5 h-5" /> : 'Confirm Cashout'}
              </Button>
              <button
                onClick={() => setStep(3)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to transaction details
              </button>
            </>
          )}
        </div>
      </div>

      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent
          overlayClassName="!bg-black/65 backdrop-blur-md !z-[100]"
          className="max-w-md border-0 p-7 sm:p-10 rounded-3xl !z-[101]"
          style={{
            background: '#FFFFFF',
            color: '#0B0F0C',
            boxShadow: '0 30px 70px rgba(0,0,0,0.45)'
          }}
        >
          <DialogHeader>
            <DialogTitle
              className="display font-extrabold text-center"
              style={{
                color: '#0B0F0C',
                fontSize: '28px',
                lineHeight: 1.15,
                letterSpacing: '-0.01em'
              }}
            >
              Are you sure you want to leave?
            </DialogTitle>
          </DialogHeader>
          <p
            className="text-center mt-4"
            style={{ color: '#1F2937', fontSize: '17px', fontWeight: 500 }}
          >
            Your transaction will be canceled if you proceed.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-7">
            <button
              onClick={() => { setConfirmCloseOpen(false); close(); }}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#E11D2E',
                boxShadow: '0 10px 24px rgba(225,29,46,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-yes-btn"
            >
              Yes, I want to go out
            </button>
            <button
              onClick={() => setConfirmCloseOpen(false)}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#15A036',
                boxShadow: '0 10px 24px rgba(21,160,54,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-continue-btn"
            >
              I want to continue
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
